import type { LobbyEvent, BackgroundRoomContext } from '@danda/shared';
import { RoomTypeEnum } from '@danda/shared';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { getStaffAvatarUrl, getVideoUrl } from '@/lib/media';
import type { EventHandlerContext } from './lobby-event-types';

/** Tavian's pre-made avatar URL */
const TAVIAN_AVATAR_URL = getStaffAvatarUrl('tavian-avatar.webp');

/** Default lobby background video (matches LobbyBackground.tsx default) */
const DEFAULT_LOBBY_VIDEO = getVideoUrl('tavian.mp4');

/**
 * Track which party images we've already added messages for.
 * This prevents duplicate welcome/update messages when the same background
 * is received multiple times (due to multiple requests or reconnects).
 * Uses a Set outside React state for synchronous checks without race conditions.
 */
const seenPartyImages = new Set<string>();

/** Clear tracked party images (call on reset/disconnect to prevent stale dedup) */
export function clearSeenPartyImages() {
  seenPartyImages.clear();
}

export function handleBackgroundEvents(event: LobbyEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:background-generating': {
      state.setGeneratingBackground(true);
      return true;
    }

    case 'lobby:background-generated': {
      const bgEvent = event as LobbyEvent & {
        mediaUrl: string;
        mediaType: string;
        roomContext: unknown;
      };

      // Runtime validation for mediaType
      if (bgEvent.mediaType !== 'video' && bgEvent.mediaType !== 'image') {
        console.error('Invalid mediaType received:', bgEvent.mediaType);
        return true;
      }

      state.setBackgroundMedia(
        bgEvent.mediaUrl,
        bgEvent.mediaType,
        bgEvent.roomContext as Parameters<typeof state.setBackgroundMedia>[2]
      );

      // If this is a party background image, update party state and add a message to the party chat
      const roomContext = bgEvent.roomContext as BackgroundRoomContext | null;
      if (
        roomContext?.roomType === RoomTypeEnum.PARTY &&
        roomContext.partyId &&
        bgEvent.mediaType === 'image'
      ) {
        // Keep party's partyImageUrl in sync with the latest generated image
        state.updateParty(roomContext.partyId, { partyImageUrl: bgEvent.mediaUrl });

        const partyRoomId = `party-${roomContext.partyId}`;
        const partyRoom = state.chatRooms.find((r) => r.id === partyRoomId);

        if (partyRoom) {
          const party = state.parties.find((p) => p.id === roomContext.partyId);
          const partyName = party?.name ?? 'your party';

          // Create a unique key for this party + image combination
          const imageKey = `${roomContext.partyId}:${bgEvent.mediaUrl}`;

          // Synchronous check using module-level Set (no race condition)
          if (seenPartyImages.has(imageKey)) {
            // Already processed this exact image for this party, skip
            return true;
          }

          // Mark as seen BEFORE adding message (prevents race condition)
          seenPartyImages.add(imageKey);

          const hasWelcomeMessage = partyRoom.messages.some((m) => m.id.startsWith('welcome-'));

          if (!hasWelcomeMessage) {
            // First time - add welcome message with party image
            state.addMessage(partyRoomId, {
              id: `welcome-${roomContext.partyId}-${Date.now()}`,
              senderId: 'tavian',
              senderName: 'Tavian',
              content: `[PARTY_IMAGE:${bgEvent.mediaUrl}]\n\n✨ Welcome to **${partyName}**! May your adventures be legendary.`,
              timestamp: new Date(),
              type: 'tavian',
              avatar: TAVIAN_AVATAR_URL,
              avatarUrl: TAVIAN_AVATAR_URL,
              avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
            });
          } else {
            // Membership changed - add party updated message with new image
            state.addMessage(partyRoomId, {
              id: `party-image-${roomContext.partyId}-${Date.now()}`,
              senderId: 'tavian',
              senderName: 'Tavian',
              content: `[PARTY_IMAGE:${bgEvent.mediaUrl}]\n\n⚔️  The awesome **${partyName}**!`,
              timestamp: new Date(),
              type: 'tavian',
              avatar: TAVIAN_AVATAR_URL,
              avatarUrl: TAVIAN_AVATAR_URL,
              avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
            });
          }
        }
      }

      return true;
    }

    case 'lobby:background-error': {
      const errEvent = event as LobbyEvent & { error: string; roomContext?: unknown };
      console.warn('Background generation failed:', errEvent.error);

      const errorRoomContext = (errEvent.roomContext ?? null) as BackgroundRoomContext | null;

      // Reset to default lobby video so user doesn't see a white screen.
      // Preserve the failed context so the LobbyBackground dedup short-circuits
      // a re-fetch of the same room — otherwise the effect re-fires every render
      // and the rate-limited request loops on itself.
      state.setBackgroundMedia(DEFAULT_LOBBY_VIDEO, 'video', errorRoomContext);

      // If this error is for a party context, still add a welcome message (without image)
      if (errorRoomContext?.roomType === RoomTypeEnum.PARTY && errorRoomContext.partyId) {
        const partyRoomId = `party-${errorRoomContext.partyId}`;
        const partyRoom = state.chatRooms.find((r) => r.id === partyRoomId);

        if (partyRoom) {
          const hasWelcomeMessage = partyRoom.messages.some((m) => m.id.startsWith('welcome-'));

          if (!hasWelcomeMessage) {
            const party = state.parties.find((p) => p.id === errorRoomContext.partyId);
            const partyName = party?.name ?? 'your party';

            state.addMessage(partyRoomId, {
              id: `welcome-${errorRoomContext.partyId}-${Date.now()}`,
              senderId: 'tavian',
              senderName: 'Tavian',
              content: `✨ Welcome to **${partyName}**! May your adventures be legendary.`,
              timestamp: new Date(),
              type: 'tavian',
              avatar: TAVIAN_AVATAR_URL,
              avatarUrl: TAVIAN_AVATAR_URL,
              avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
            });
          }
        }
      }

      return true;
    }

    case 'lobby:portrait-generating': {
      state.setGeneratingPortrait(true);
      return true;
    }

    case 'lobby:portrait-generated': {
      const portraitEvent = event as LobbyEvent & {
        imageUrl: string;
        avatarUrl?: string;
        mediaType?: 'image' | 'video';
        characterId: string;
      };
      // Default to 'image' for backwards compatibility
      state.setCharacterPortrait(portraitEvent.imageUrl, portraitEvent.mediaType ?? 'image');

      // Update character avatar if provided
      if (portraitEvent.avatarUrl && portraitEvent.characterId) {
        state.updateCharacter(portraitEvent.characterId, {
          avatarUrl: portraitEvent.avatarUrl,
          avatar: portraitEvent.avatarUrl,
        });
      }
      if (portraitEvent.characterId) {
        // Sync portrait + avatar to inner voice room
        const innerRoomId = `inner-${portraitEvent.characterId}`;
        if (state.chatRooms.some((r) => r.id === innerRoomId)) {
          const roomUpdates: Record<string, string> = { portraitUrl: portraitEvent.imageUrl };
          if (portraitEvent.avatarUrl) {
            roomUpdates.avatarUrl = portraitEvent.avatarUrl;
          }
          state.updateChatRoom(innerRoomId, roomUpdates);
        }

        // Patch portraitUrl (and avatarUrl when the event carries it) on every
        // party where this character is a member — otherwise click-to-enlarge
        // and the avatar circle stay stale until the next party refresh.
        const memberPatch: { portraitUrl: string; avatarUrl?: string } = {
          portraitUrl: portraitEvent.imageUrl,
        };
        if (portraitEvent.avatarUrl) memberPatch.avatarUrl = portraitEvent.avatarUrl;

        state.parties.forEach((party) => {
          if (!party.members?.some((m) => m.id === portraitEvent.characterId)) return;
          const updatedMembers = party.members.map((m) =>
            m.id === portraitEvent.characterId ? { ...m, ...memberPatch } : m
          );
          state.updateParty(party.id, { members: updatedMembers });
        });

        // Patch portraitUrl on the lobbyCharacters entry (dashboard + char pickers)
        if (state.lobbyCharacters.some((c) => c.id === portraitEvent.characterId)) {
          state.setLobbyCharacters(
            state.lobbyCharacters.map((c) =>
              c.id === portraitEvent.characterId ? { ...c, ...memberPatch } : c
            )
          );
        }
      }
      return true;
    }

    case 'lobby:portrait-error': {
      const errEvent = event as LobbyEvent & { error: string };
      state.setPortraitError(errEvent.error);
      return true;
    }

    default:
      return false;
  }
}
